import React, { useMemo, useState } from 'react';
import { Minus, Plus, Search, X } from 'lucide-react';
import { useCatalog } from '../hooks/useCatalog';
import { formatPrice, matchesSearch } from '../utils/catalog';
import { addOrderLine, setOrderLineQty } from '../utils/orders';

const MAX_RESULTS = 6;

// Buscador de productos del catálogo para el pedido manual: al elegir uno entra a la lista con su precio, y el padre recalcula el total.
export default function ManualOrderProducts({ lines, onChange }) {
  const { products, priceMap, defaultKey, loading, tableMissing } = useCatalog();
  const [query, setQuery] = useState('');

  const priced = useMemo(
    () => products.filter((p) => p.active !== false && priceMap.get(p.id)?.[defaultKey] !== undefined),
    [products, priceMap, defaultKey]
  );
  const results = query.trim() ? priced.filter((p) => matchesSearch(p, query)).slice(0, MAX_RESULTS) : [];

  if (loading) return null;
  if (tableMissing || !priced.length) {
    return <p className="text-[10px] text-seryi-text">Para elegir productos con su precio, créalos primero en «Catálogo y precios».</p>;
  }

  const pick = (p) => { onChange(addOrderLine(lines, p, priceMap.get(p.id)[defaultKey])); setQuery(''); };

  return (
    <div className="space-y-2">
      <label className="text-[10px] uppercase font-bold text-seryi-text">Productos del catálogo</label>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-seryi-text" />
        <input
          type="text" aria-label="Buscar producto" placeholder="Busca por nombre o código (ej: garrafón)"
          className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl py-2.5 pl-9 pr-3 text-xs text-seryi-heading"
          value={query} onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {query.trim() && (
        <ul className="bg-seryi-bg-dark border border-seryi-bg-light rounded-xl divide-y divide-seryi-bg-light overflow-hidden">
          {results.length === 0 && <li className="px-3 py-2 text-xs text-seryi-text">Ningún producto coincide.</li>}
          {results.map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => pick(p)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs text-seryi-heading hover:bg-seryi-bg-light/40">
                <span className="truncate">{p.name}</span>
                <span className="shrink-0 font-bold text-seryi-green">{formatPrice(priceMap.get(p.id)[defaultKey])}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {lines.length > 0 && (
        <ul className="space-y-1.5">
          {lines.map((l) => (
            <li key={l.id} className="flex items-center gap-2 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading">
              <span className="flex-1 min-w-0 truncate">{l.nombre}</span>
              <button type="button" aria-label={`Menos ${l.nombre}`} onClick={() => onChange(setOrderLineQty(lines, l.id, l.cantidad - 1))} className="p-1 rounded-lg bg-seryi-bg-light/40 hover:bg-seryi-bg-light"><Minus size={12} /></button>
              <span className="w-6 text-center font-bold" aria-label={`Cantidad ${l.nombre}`}>{l.cantidad}</span>
              <button type="button" aria-label={`Más ${l.nombre}`} onClick={() => onChange(setOrderLineQty(lines, l.id, l.cantidad + 1))} className="p-1 rounded-lg bg-seryi-bg-light/40 hover:bg-seryi-bg-light"><Plus size={12} /></button>
              <span className="w-20 text-right font-bold">{formatPrice(l.cantidad * l.precio)}</span>
              <button type="button" aria-label={`Quitar ${l.nombre}`} onClick={() => onChange(setOrderLineQty(lines, l.id, 0))} className="text-slate-400 hover:text-white"><X size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
